import React, { useEffect, useState } from 'react';
import { AdminShell } from '../../components/AdminShell';
import { supabase } from '../../lib/supabase';
import { Plug, Save, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Platform admin — Integrations page.
 *
 * Ops surface for 3rd-party service credentials that live in
 * `integrations` (add-sms-provider-fields.sql). Backend does the
 * masking — secrets never travel to the browser once saved, so
 * re-loading the page shows `••••abcd` on the auth-token field. If
 * the operator leaves that masked value alone and hits Save, the
 * backend keeps the stored value.
 *
 * Adding a new integration = add an entry to the backend REGISTRY
 * dict; this page reads its structure from GET /api/integrations.
 */
const IntegrationsInner = () => {
  const [items, setItems] = useState(null);   // null = loading
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    const { data: { session } } = await supabase.auth.getSession();
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${BACKEND}/api/integrations`, {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.detail || `HTTP ${res.status}`);
      setItems([]);
      return;
    }
    setItems(data.integrations || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plug size={18} /> Integrations
          </h2>
          <div className="sub">
            3rd-party service credentials. Only master admins can view or change these.
            Secrets are stored server-side and never sent back to the browser once saved.
          </div>
        </div>
      </div>
      <div className="body">
        {error && (
          <div className="statebox" style={{ marginBottom: 14 }} data-testid="integrations-error">
            <h5>Couldn&apos;t load integrations</h5>
            <p>{error}</p>
          </div>
        )}
        {items === null ? null : items.length === 0 ? (
          <div className="statebox">
            <h5>No integrations registered</h5>
            <p>Nothing to configure yet.</p>
          </div>
        ) : (
          items.map(item => (
            <IntegrationPanel
              key={item.key}
              item={item}
              onSaved={load}
            />
          ))
        )}
      </div>
    </main>
  );
};

const IntegrationPanel = ({ item, onSaved }) => {
  // `draft` seeds from the server-masked values so the operator sees
  // "••••abcd" on secret fields and the plaintext for non-secret ones.
  const [draft, setDraft] = useState(() => ({ ...(item.fields || {}) }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setMsg(''); setErr(''); setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND}/api/integrations/${item.key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ fields: draft }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setMsg('Saved.');
      // Re-seed local draft from the server response so masked fields
      // reflect the new stored value's last-4.
      setDraft({ ...(data.fields || {}) });
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearField = (k) => setDraft(d => ({ ...d, [k]: null }));

  return (
    <div className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid={`integration-panel-${item.key}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <b style={{ fontSize: 14 }}>{item.label}</b>
        {item.ready ? (
          <span className="pill green" data-testid={`integration-status-${item.key}`}>
            <CheckCircle2 size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            Configured
          </span>
        ) : (
          <span className="pill amber" data-testid={`integration-status-${item.key}`}>
            <AlertCircle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            Not configured
          </span>
        )}
        {item.updated_at && (
          <span className="hint" style={{ marginLeft: 'auto', fontSize: 11 }}>
            Updated {new Date(item.updated_at).toLocaleString('en-GB')}
          </span>
        )}
      </div>

      {Object.keys(item.fields || {}).map(field => {
        const value = draft[field] == null ? '' : draft[field];
        const help = (item.help || {})[field];
        // Show a "Clear" affordance next to any field that currently
        // has a stored value so operators can wipe a stale credential.
        const hasStored = !!(item.fields || {})[field];
        const enumOptions = (item.enums || {})[field];
        return (
          <div className="field" key={field} style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
              {field.replace(/_/g, ' ')}
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              {enumOptions ? (
                <select
                  className="inp"
                  value={value}
                  onChange={(e) => setField(field, e.target.value)}
                  data-testid={`integration-${item.key}-${field}`}
                  style={{ flex: 1 }}
                  disabled={!!item.read_only}
                >
                  {enumOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt === 'simulator' ? 'Simulator (log-only, no SMS sent)'
                        : opt === 'twilio' ? 'Twilio (live SMS)'
                        : opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="inp"
                  value={value}
                  onChange={(e) => setField(field, e.target.value)}
                  placeholder={hasStored ? '' : `Enter ${field.replace(/_/g, ' ')}`}
                  data-testid={`integration-${item.key}-${field}`}
                  style={{ flex: 1 }}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={!!item.read_only}
                />
              )}
              {hasStored && !item.read_only && !enumOptions && (
                <button
                  type="button"
                  className="btn s"
                  onClick={() => clearField(field)}
                  data-testid={`integration-${item.key}-${field}-clear`}
                  title="Clear this value on save"
                >Clear</button>
              )}
            </div>
            {help && <div className="hint" style={{ marginTop: 4, fontSize: 11 }}>{help}</div>}
          </div>
        );
      })}

      {item.read_only ? (
        <div className="hint" style={{ marginTop: 14, fontSize: 11, fontStyle: 'italic' }}>
          Read-only — this integration&apos;s credentials live in <code>backend/.env</code>. Edit there and restart the backend to rotate.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
          <button
            className="btn p"
            onClick={save}
            disabled={saving}
            data-testid={`integration-${item.key}-save`}
          >
            <Save size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {msg && <span className="hint" style={{ color: 'var(--ok)' }} data-testid={`integration-${item.key}-msg`}>{msg}</span>}
          {err && <span className="hint" style={{ color: 'var(--bad)' }} data-testid={`integration-${item.key}-err`}>{err}</span>}
        </div>
      )}
    </div>
  );
};

const Integrations = () => (
  <AdminShell>
    <IntegrationsInner />
  </AdminShell>
);
export default Integrations;
